import * as React from 'react';

import { Loader2 } from 'lucide-react';

// ─── Props ────────────────────────────────────────────────────────────────────

interface LoadMoreSentinelProps {
  /** Whether more pages are available to load. */
  hasMore: boolean;
  /** Whether a subsequent page is currently being loaded. */
  isLoadingMore: boolean;
  /** Invoked when the sentinel scrolls into view and more pages are available. */
  onLoadMore: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Invisible scroll anchor placed at the bottom of a paginated list. When it
 * enters the viewport and more pages exist, it triggers loading the next page.
 */
export function LoadMoreSentinel({
  hasMore,
  isLoadingMore,
  onLoadMore,
}: LoadMoreSentinelProps): React.JSX.Element | null {
  const sentinelRef = React.useRef<HTMLDivElement>(null);
  const onLoadMoreRef = React.useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { rootMargin: '120px' },
    );

    observer.observe(node);

    return (): void => observer.disconnect();
  }, [hasMore, isLoadingMore]);

  if (!hasMore && !isLoadingMore) {
    return null;
  }

  return (
    <div className="flex justify-center py-4" ref={sentinelRef}>
      {isLoadingMore ? <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /> : null}
    </div>
  );
}
